import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sparkles, Target, Settings, Trash2 } from 'lucide-react';
import { ModeSelector, type ECOMode } from './ModeSelector';
import { TemplateSelector } from './TemplateSelector';
import { FileUploader } from './FileUploader';
import { ecoTemplates } from '@/data/ecoTemplates';
import type { UploadedFile } from './types';

interface ECOSidebarProps {
  mode: ECOMode;
  selectedTemplate: string;
  uploadedFiles: UploadedFile[];
  onModeChange: (mode: ECOMode) => void;
  onTemplateSelect: (templateId: string) => void;
  onFilesChange: (files: UploadedFile[]) => void;
  onRemoveFile: (fileId: string) => void;
  onClearChat: () => void;
  messagesCount: number;
}

export const ECOSidebar: React.FC<ECOSidebarProps> = ({
  mode,
  selectedTemplate,
  uploadedFiles,
  onModeChange,
  onTemplateSelect,
  onFilesChange,
  onRemoveFile,
  onClearChat,
  messagesCount
}) => {
  return (
    <div className="w-80 min-h-screen bg-card/50 border-r border-border/60 backdrop-blur-sm">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">
            ECO Assistant
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            AI-powered content enhancement
          </p>
        </div>

        <Separator />

        {/* Enhancement Mode */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Settings className="h-4 w-4" />
            Enhancement Mode
          </div>
          <div className="space-y-2">
            <Button
              variant={mode === 'entire' ? 'default' : 'ghost'}
              onClick={() => onModeChange('entire')}
              className={`w-full justify-start gap-3 h-auto p-3 ${
                mode === 'entire' 
                  ? 'bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white' 
                  : 'hover:bg-muted'
              }`}
            >
              <Sparkles className="h-4 w-4" />
              <div className="flex flex-col items-start text-left">
                <span className="font-medium">Complete ECO</span>
                <span className="text-xs opacity-75">All 7 enhancement blocks</span>
              </div>
            </Button>
            
            <Button
              variant={mode === 'individual' ? 'default' : 'ghost'}
              onClick={() => onModeChange('individual')}
              className={`w-full justify-start gap-3 h-auto p-3 ${
                mode === 'individual' 
                  ? 'bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white' 
                  : 'hover:bg-muted'
              }`}
            >
              <Target className="h-4 w-4" />
              <div className="flex flex-col items-start text-left">
                <span className="font-medium">Individual Style</span>
                <span className="text-xs opacity-75">Select specific enhancement</span>
              </div>
            </Button>
          </div>
        </div>

        <Separator />

        {/* Template Selection */}
        {mode === 'individual' && (
          <>
            <div className="space-y-3">
              <div className="text-sm font-medium text-foreground">Enhancement Style</div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {ecoTemplates
                  .filter(template => template.id !== 'entire_eco')
                  .map((template) => (
                    <Button
                      key={template.id}
                      variant={selectedTemplate === template.id ? 'secondary' : 'ghost'}
                      onClick={() => onTemplateSelect(template.id)}
                      className="w-full justify-start p-3 h-auto text-left"
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-sm">{template.label}</span>
                        <span className="text-xs text-muted-foreground">{template.description}</span>
                      </div>
                    </Button>
                  ))}
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* File Upload */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-foreground">Context Files</div>
          <FileUploader
            uploadedFiles={uploadedFiles}
            onFilesChange={onFilesChange}
            onRemoveFile={onRemoveFile}
          />
        </div>

        <Separator />

        {/* Chat Actions */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-foreground">Chat Actions</div>
          <Button
            variant="outline"
            onClick={onClearChat}
            disabled={messagesCount === 0}
            className="w-full justify-start gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Clear Chat
          </Button>
        </div>
      </div>
    </div>
  );
};